import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Checkbox,
  Card,
  message,
  Input,
  Button,
  Space,
  Upload,
  Typography,
  Alert,
  Tabs,
  Tag,
  Collapse,
  Modal,
  Form,
  Radio,
  Divider,
  Row,
  Col,
  Spin,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  UploadOutlined,
  SendOutlined,
  UnorderedListOutlined,
  HistoryOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { isNoticiaEnabledForCoop } from "../../../utils/noticiaAccess.js";

const { TextArea } = Input;
const { Text, Title } = Typography;

const destCols = [
  { title: "Teléfono", dataIndex: "numero", key: "numero" },
  { title: "Cuenta", dataIndex: "cuenta", key: "cuenta" },
  { title: "Nombre", dataIndex: "nombre", key: "nombre" },
];

const normPhone = (n) => String(n || "").replace(/\D/g, "");

/** Cuenta sentinela: el backend ignora filas sin `cuenta`; los ingresos manuales no tienen cuenta real. */
const CUENTA_MANUAL = "(manual)";

/** Móvil AR en formato WhatsApp: 549 + 10–12 dígitos (≈13–15 en total). */
const isValidAr549Mobile = (full) => /^549\d{10,12}$/.test(full);

/**
 * Normaliza a prefijo internacional 549 (Argentina, celular).
 * Acepta número ya completo con 549, con 54, con 9 inicial o solo código de área + abonado.
 */
const normalizeTo549Ar = (raw) => {
  let d = normPhone(raw).replace(/^0+/, "");
  if (d.startsWith("15") && d.length >= 11) d = d.slice(2);

  if (!d) {
    return { ok: false, message: "Ingresá un número de teléfono." };
  }

  if (d.startsWith("549")) {
    return isValidAr549Mobile(d)
      ? { ok: true, digits: d }
      : {
          ok: false,
          message:
            "El número con 549 no es válido: revisá código de área y número (deben ser entre 10 y 12 dígitos después de 549).",
        };
  }

  if (d.startsWith("54")) {
    const rest = d.slice(2);
    const full = rest.startsWith("9") ? `549${rest.slice(1)}` : `549${rest}`;
    return isValidAr549Mobile(full)
      ? { ok: true, digits: full }
      : {
          ok: false,
          message:
            "No se pudo armar un número válido con prefijo 549. Ingresá código de área sin 0 y el número móvil.",
        };
  }

  if (d.startsWith("9")) {
    const full = `54${d}`;
    return isValidAr549Mobile(full)
      ? { ok: true, digits: full }
      : {
          ok: false,
          message: "El número que empieza con 9 no coincide con un móvil argentino válido (largo incorrecto).",
        };
  }

  const full = `549${d}`;
  return isValidAr549Mobile(full)
    ? { ok: true, digits: full }
    : {
        ok: false,
        message:
          "Formato incorrecto: ingresá el celular con código de área (sin 0) y número, o el número completo con 549.",
      };
};

/**
 * El mismo móvil en la lista superior puede venir solo con código de área (sin 549) o ya internacional.
 * `pk549` debe ser el resultado de {@link normalizeTo549Ar} para el ingreso manual.
 */
const findCoincidenciasEnListaOrigen = (pk549, rows) =>
  (rows || []).filter((row) => {
    const fromRow = normalizeTo549Ar(row.numero);
    if (fromRow.ok && fromRow.digits === pk549) {
      return true;
    }
    return normPhone(row.numero) === pk549;
  });

/**
 * El API a veces devuelve la misma fila teléfono+cuenta repetida (distinto texto de nombre).
 * Una sola fila por par (teléfono normalizado + cuenta); si chocan, queda el nombre más largo.
 */
const dedupeOrigenMensajes = (rows) => {
  const byKey = new Map();
  for (const r of rows || []) {
    const cuenta = String(r.cuenta || "").trim();
    const pk = normPhone(r.numero);
    if (!pk || !cuenta) continue;
    const k = `${pk}|${cuenta.toLowerCase()}`;
    const nombre = String(r.nombre || "").trim();
    const prev = byKey.get(k);
    if (!prev) {
      byKey.set(k, {
        ...r,
        key: `orig-${pk}-${cuenta}`,
        numero: String(r.numero || "").trim(),
        cuenta,
        nombre,
      });
      continue;
    }
    if (nombre.length > String(prev.nombre || "").length) {
      byKey.set(k, {
        ...r,
        key: prev.key,
        numero: String(r.numero || "").trim(),
        cuenta,
        nombre,
      });
    }
  }
  return Array.from(byKey.values());
};

/** Un teléfono solo puede aparecer una vez en la lista de seleccionados. */
const dedupeListaPorTelefono = (rows) => {
  const seen = new Set();
  const out = [];
  for (const r of rows || []) {
    const k = normPhone(r.numero);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push({
      ...r,
      key: `sel-${k}`,
      numero: String(r.numero || "").trim(),
      cuenta: String(r.cuenta || "").trim(),
      nombre: r.nombre || "",
    });
  }
  return out;
};

const CDifundirNoticia = () => {
  const [activeTab, setActiveTab] = useState("lista");

  const [mensajes, setMensajes] = useState([]);
  const [filteredMensajes, setFilteredMensajes] = useState([]);
  const [selectedNumeros, setSelectedNumeros] = useState([]);

  const [searchNumero, setSearchNumero] = useState("");
  const [searchCuenta, setSearchCuenta] = useState("");
  const [searchNombre, setSearchNombre] = useState("");
  const [selectedSearchNumero, setSelectedSearchNumero] = useState("");
  const [selectedSearchCuenta, setSelectedSearchCuenta] = useState("");
  const [selectedSearchNombre, setSelectedSearchNombre] = useState("");
  const [modalManualOpen, setModalManualOpen] = useState(false);
  const [formManual] = Form.useForm();
  const [manualCoincidencias, setManualCoincidencias] = useState([]);
  const [manualCuentaSeleccionada, setManualCuentaSeleccionada] = useState(null);

  const [cuerpoTexto, setCuerpoTexto] = useState("");
  const [mediaKey, setMediaKey] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [mediaFileName, setMediaFileName] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const [historial, setHistorial] = useState([]);
  const [historialLoading, setHistorialLoading] = useState(false);
  const [vistaDetalleActiva, setVistaDetalleActiva] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);

  const user = JSON.parse(localStorage.getItem("user"));
  const empresaId = user?.user?.idCooperativa;
  const enabled = isNoticiaEnabledForCoop(empresaId);
  const api = process.env.REACT_APP_API_URL;

  const loadListaOrigen = useCallback(async () => {
    const mensajesResponse = await axios.get(`${api}/mensajes/${empresaId}`, {
      headers: { Authorization: `Bearer ${user.token}` },
    });

    const datosTransformados = mensajesResponse.data.flatMap((m) =>
      m.datosCuenta.map((cuenta) => ({
        key: `${m.numero}-${cuenta.cuenta}`,
        numero: String(m.numero || "").trim(),
        cuenta: String(cuenta.cuenta || "").trim(),
        nombre: cuenta.nombre || "",
      }))
    );

    setMensajes(dedupeOrigenMensajes(datosTransformados));

    try {
      const guardados = await axios.get(`${api}/difundir-noticia/${empresaId}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (guardados.data?.telefonos?.length) {
        const telefonos = dedupeListaPorTelefono(
          guardados.data.telefonos.map((tel) => ({
            key: `${tel.numero}-${tel.cuenta}`,
            numero: String(tel.numero || "").trim(),
            cuenta: String(tel.cuenta || "").trim(),
            nombre: tel.nombre || "",
          }))
        );
        setSelectedNumeros(telefonos);
      }
    } catch (e) {
      if (e?.response?.status !== 404) throw e;
    }
  }, [api, empresaId, user.token]);

  const loadHistorial = useCallback(async () => {
    setHistorialLoading(true);
    try {
      const res = await axios.get(`${api}/difundir-noticia/${empresaId}/historial`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setHistorial(res.data || []);
    } catch (e) {
      console.error(e);
      message.error("No se pudo cargar el historial.");
    } finally {
      setHistorialLoading(false);
    }
  }, [api, empresaId, user.token]);

  useEffect(() => {
    if (!enabled) return;
    loadListaOrigen().catch((error) => {
      console.error("Error al cargar datos:", error);
      message.error("Error al cargar los datos.");
    });
  }, [enabled, loadListaOrigen]);

  useEffect(() => {
    if (activeTab === "historial" && enabled) {
      loadHistorial();
    }
  }, [activeTab, enabled, loadHistorial]);

  const isRecordSelected = (record) =>
    selectedNumeros.some((item) => normPhone(item.numero) === normPhone(record.numero));

  const applyFilter = (data) =>
    data.filter((item) => {
      const numero = String(item.numero || "").toLowerCase();
      const cuenta = String(item.cuenta || "").toLowerCase();
      const nombre = String(item.nombre || "").toLowerCase();
      return (
        (!searchNumero || numero.includes(searchNumero.toLowerCase())) &&
        (!searchCuenta || cuenta.includes(searchCuenta.toLowerCase())) &&
        (!searchNombre || nombre.includes(searchNombre.toLowerCase()))
      );
    });

  const applySelectedFilter = (data) =>
    data.filter((item) => {
      const numero = String(item.numero || "").toLowerCase();
      const cuenta = String(item.cuenta || "").toLowerCase();
      const nombre = String(item.nombre || "").toLowerCase();
      return (
        (!selectedSearchNumero || numero.includes(selectedSearchNumero.toLowerCase())) &&
        (!selectedSearchCuenta || cuenta.includes(selectedSearchCuenta.toLowerCase())) &&
        (!selectedSearchNombre || nombre.includes(selectedSearchNombre.toLowerCase()))
      );
    });

  useEffect(() => {
    setFilteredMensajes(applyFilter(mensajes));
  }, [mensajes, searchNumero, searchCuenta, searchNombre]);

  const filteredSelectedNumeros = applySelectedFilter(selectedNumeros);

  const handleCheckboxChange = (record) => {
    const rk = normPhone(record.numero);
    if (isRecordSelected(record)) {
      setSelectedNumeros(selectedNumeros.filter((item) => normPhone(item.numero) !== rk));
      return;
    }
    if (selectedNumeros.some((item) => normPhone(item.numero) === rk)) {
      message.warning("Este número ya está en la lista de seleccionados.");
      return;
    }
    setSelectedNumeros(
      dedupeListaPorTelefono([
        ...selectedNumeros,
        {
          ...record,
          key: `sel-${rk}`,
          numero: String(record.numero || "").trim(),
          cuenta: String(record.cuenta || "").trim(),
          nombre: record.nombre || "",
        },
      ])
    );
  };

  const handleSelectAll = (selected) => {
    if (selected) {
      const merged = [...selectedNumeros];
      filteredMensajes.forEach((record) => {
        merged.push({
          ...record,
          numero: String(record.numero || "").trim(),
          cuenta: String(record.cuenta || "").trim(),
          nombre: record.nombre || "",
        });
      });
      setSelectedNumeros(dedupeListaPorTelefono(merged));
      return;
    }
    const visibleKeys = new Set(filteredMensajes.map((r) => normPhone(r.numero)));
    setSelectedNumeros(selectedNumeros.filter((item) => !visibleKeys.has(normPhone(item.numero))));
  };

  const handleRemoveNumero = (numero) => {
    const k = normPhone(numero);
    setSelectedNumeros(selectedNumeros.filter((item) => normPhone(item.numero) !== k));
  };

  const openModalManual = () => {
    formManual.resetFields();
    setManualCoincidencias([]);
    setManualCuentaSeleccionada(null);
    setModalManualOpen(true);
  };

  /** Sin Promise.reject en el Modal: CRA mostraba overlay rojo al rechazar onOk con Error. */
  const submitManualRecipient = (values) => {
    const { nombre, telefono } = values;
    const trimmedName = String(nombre || "").trim();
    const normalized = normalizeTo549Ar(telefono);
    if (!normalized.ok) {
      message.warning(normalized.message);
      return;
    }
    const pk = normalized.digits;
    if (selectedNumeros.some((item) => normPhone(item.numero) === pk)) {
      message.warning("Este número ya está en la lista de seleccionados.");
      return;
    }

    const coincidencias = findCoincidenciasEnListaOrigen(pk, mensajes);
    if (coincidencias.length > 0) {
      if (!manualCuentaSeleccionada) {
        setManualCoincidencias(coincidencias);
        message.info("Elegí una cuenta de la lista encontrada para continuar.");
        return;
      }

      const seleccion = coincidencias.find((row) => row.key === manualCuentaSeleccionada);
      if (!seleccion) {
        message.warning("La cuenta elegida no es válida. Volvé a seleccionarla.");
        return;
      }

      setSelectedNumeros(
        dedupeListaPorTelefono([
          ...selectedNumeros,
          {
            key: `sel-${pk}`,
            numero: pk,
            cuenta: String(seleccion.cuenta || "").trim(),
            nombre: seleccion.nombre || "",
          },
        ])
      );
      message.success("Destinatario agregado desde coincidencias existentes.");
      setModalManualOpen(false);
      formManual.resetFields();
      setManualCoincidencias([]);
      setManualCuentaSeleccionada(null);
      return;
    }

    setSelectedNumeros(
      dedupeListaPorTelefono([
        ...selectedNumeros,
        {
          key: `sel-${pk}`,
          numero: pk,
          cuenta: CUENTA_MANUAL,
          nombre: trimmedName,
        },
      ])
    );
    message.success("Destinatario agregado a la lista.");
    setModalManualOpen(false);
    formManual.resetFields();
    setManualCoincidencias([]);
    setManualCuentaSeleccionada(null);
  };

  const handleModalManualCancel = () => {
    setModalManualOpen(false);
    formManual.resetFields();
    setManualCoincidencias([]);
    setManualCuentaSeleccionada(null);
  };

  const handleGuardarLista = async () => {
    if (selectedNumeros.length === 0) {
      message.error("Debe seleccionar al menos un número.");
      return;
    }
    try {
      const telefonosLimpios = dedupeListaPorTelefono(selectedNumeros).map(({ numero, cuenta, nombre }) => ({
        numero,
        cuenta,
        nombre,
      }));
      await axios.post(
        `${api}/difundir-noticia`,
        {
          idCooperativa: empresaId,
          telefonos: telefonosLimpios,
        },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        }
      );
      message.success("Lista guardada correctamente.");
      setSelectedNumeros(dedupeListaPorTelefono(selectedNumeros));
    } catch (error) {
      console.error(error);
      message.error("Error al guardar la lista.");
    }
  };

  const customUpload = async ({ file, onSuccess, onError }) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("idCooperativa", empresaId);
    try {
      const res = await axios.post(`${api}/difundir-noticia/media`, fd, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      const ct = res.data.contentType || file.type;
      setMediaKey(res.data.key);
      setMediaType(ct === "application/pdf" ? "document" : "image");
      setMediaFileName(res.data.originalName || file.name);
      onSuccess(res.data);
      message.success("Archivo listo para enviar.");
    } catch (e) {
      onError(e);
      message.error("No se pudo subir el archivo.");
    }
  };

  const handleEnviar = async () => {
    const texto = cuerpoTexto.trim();
    if (!texto && !mediaKey) {
      message.error("Escribí un mensaje o adjuntá un archivo (o ambos).");
      return;
    }
    if (selectedNumeros.length === 0) {
      message.error("Seleccioná al menos un destinatario (pestaña Lista).");
      return;
    }

    setEnviando(true);
    try {
      const response = await axios.post(
        `${api}/difundir-noticia/enviar`,
        {
          idCooperativa: empresaId,
          telefonos: dedupeListaPorTelefono(selectedNumeros).map(({ numero, cuenta, nombre }) => ({
            numero,
            cuenta,
            nombre,
          })),
          text: texto || undefined,
          mediaKey: mediaKey || undefined,
          mediaType: mediaKey ? mediaType : undefined,
          filename: mediaFileName || "documento.pdf",
        },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const warnings = response?.data?.warnings || [];
      warnings.forEach((w) => message.warning(w));

      const sync = response?.data?.syncSummary;
      if (sync?.appliedErr > 0) {
        message.warning(
          `Hubo ${sync.appliedErr} destinatario(s) con error de envío de plantilla. Podés revisarlo en el historial (Ver detalle).`
        );
      }

      if (sync && sync.expected > 0 && sync.matched === sync.expected && sync.appliedErr === 0) {
        message.success(
          `Plantilla registrada para ${sync.matched} número(s). El historial se actualizará con las respuestas SI/NO.`
        );
      } else if (sync && sync.matched > 0) {
        message.success(
          `Sincronizado para ${sync.matched} de ${sync.expected} número(s). Si falta gente en «Sin confirmar envío», revisá el formato de números en la respuesta del bot.`
        );
      } else if (!warnings.length) {
        message.success(
          `Campaña registrada. El historial se actualizará con las respuestas SI/NO.`
        );
      }
      setActiveTab("historial");
      loadHistorial();
    } catch (error) {
      const msg = error?.response?.data?.message || "Error al enviar.";
      message.error(msg);
    } finally {
      setEnviando(false);
    }
  };

  const closeDetalleVista = () => {
    setVistaDetalleActiva(false);
    setDetalle(null);
    setDetalleLoading(false);
    setActiveTab("historial");
  };

  const openDetalle = async (campaignId) => {
    setVistaDetalleActiva(true);
    setDetalle(null);
    setDetalleLoading(true);
    try {
      const res = await axios.get(`${api}/difundir-noticia/${empresaId}/historial/${campaignId}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setDetalle(res.data);
    } catch (e) {
      message.error("No se pudo cargar el detalle.");
      closeDetalleVista();
    } finally {
      setDetalleLoading(false);
    }
  };

  const areAllSelected =
    filteredMensajes.length > 0 && filteredMensajes.every((r) => isRecordSelected(r));

  const columns = [
    {
      title: "",
      key: "checkbox",
      width: 48,
      render: (_, record) => (
        <Checkbox checked={isRecordSelected(record)} onChange={() => handleCheckboxChange(record)} />
      ),
    },
    ...destCols,
  ];

  const selectedColumns = [
    ...destCols,
    {
      title: "",
      key: "acc",
      render: (_, record) => (
        <Button type="link" danger onClick={() => handleRemoveNumero(record.numero)}>
          Quitar
        </Button>
      ),
    },
  ];

  const historialColumns = [
    {
      title: "Fecha",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (d) => (d ? new Date(d).toLocaleString("es-AR") : "—"),
    },
    {
      title: "Resumen",
      dataIndex: "textoPreview",
      key: "textoPreview",
      ellipsis: true,
      width: "28%",
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      width: 70,
    },
    {
      title: (
        <Tooltip title="Destinatarios a los que el dashboard aún no registró resultado del envío de plantilla (el bot no devolvió detalle por número o falló el emparejamiento).">
          Sin confirmar envío
        </Tooltip>
      ),
      dataIndex: "pendiente_envio",
      key: "pe",
      width: 118,
      render: (v) => v ?? 0,
    },
    {
      title: (
        <Tooltip title="Ya recibieron plantilla OK (esperando SÍ/NO, respondieron, o venció el plazo sin respuesta). No incluye errores de envío ni pendientes sin confirmar.">
          En seguimiento
        </Tooltip>
      ),
      key: "ok",
      width: 118,
      render: (_, row) =>
        (row.si || 0) + (row.no || 0) + (row.sin_respuesta || 0) + (row.pendiente || 0),
    },
    { title: "Sí", dataIndex: "si", key: "si", width: 56 },
    { title: "No", dataIndex: "no", key: "no", width: 56 },
    { title: "Sin resp.", dataIndex: "sin_respuesta", key: "sr", width: 88 },
    { title: "Err. envío", dataIndex: "error_plantilla", key: "err", width: 96 },
    {
      title: "",
      key: "go",
      width: 100,
      render: (_, row) => (
        <Button type="link" onClick={() => openDetalle(row._id)}>
          Ver detalle
        </Button>
      ),
    },
  ];

  const tablePagination = { pageSize: 25, showSizeChanger: true, pageSizeOptions: [15, 25, 50, 100], showTotal: (t) => `${t} filas` };

  const renderVistaDetalleCompleta = () => {
    if (detalleLoading) {
      return (
        <div style={{ textAlign: "center", padding: "80px 24px" }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Cargando detalle de la campaña…</Text>
          </div>
        </div>
      );
    }

    if (!detalle) {
      return null;
    }

    const { destinatarios = [], counts = {}, textoPreview, plantillaNombre, createdAt, expiresAt } = detalle;

    const conPlantillaOk = destinatarios.filter((d) => d.estadoVista !== "error_plantilla" && d.estadoVista !== "pendiente_envio");
    const si = destinatarios.filter((d) => d.estadoVista === "si");
    const no = destinatarios.filter((d) => d.estadoVista === "no");
    const sinResp = destinatarios.filter((d) => d.estadoVista === "sin_respuesta");
    const errEnvio = destinatarios.filter((d) => d.estadoVista === "error_plantilla");

    const errCols = [
      ...destCols,
      { title: "Motivo", dataIndex: "errorPlantilla", key: "err", ellipsis: true },
    ];

    const estadoCol = {
      title: "Estado",
      key: "st",
      width: 160,
      render: (_, r) => {
        const labels = {
          pendiente: <Tag color="processing">Esperando respuesta</Tag>,
          si: <Tag color="success">Respondió SÍ</Tag>,
          no: <Tag color="orange">Respondió NO</Tag>,
          sin_respuesta: <Tag>Sin respuesta</Tag>,
        };
        return labels[r.estadoVista] || <Tag>{r.estadoVista}</Tag>;
      },
    };

    return (
      <>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={8}>
            <Card size="small" title="Fecha de envío" style={{ borderRadius: 10, height: "100%" }} styles={{ body: { padding: "16px 18px" } }}>
              <Text style={{ fontSize: 16, fontWeight: 500 }}>
                {createdAt ? new Date(createdAt).toLocaleString("es-AR") : "—"}
              </Text>
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card size="small" title="Vencimiento espera respuesta" style={{ borderRadius: 10, height: "100%" }} styles={{ body: { padding: "16px 18px" } }}>
              <Text style={{ fontSize: 16, fontWeight: 500 }}>
                {expiresAt ? new Date(expiresAt).toLocaleString("es-AR") : "—"}
              </Text>
              <Text type="secondary" style={{ display: "block", marginTop: 10, fontSize: 12, lineHeight: 1.5 }}>
                Pasada esta fecha, quien no respondió figura como “sin respuesta” (48 h desde el envío).
              </Text>
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card size="small" title="Plantilla" style={{ borderRadius: 10, height: "100%" }} styles={{ body: { padding: "16px 18px" } }}>
              <Tag color="blue" style={{ fontSize: 13, padding: "4px 10px" }}>
                {plantillaNombre || "noticia_coop_utility"}
              </Tag>
            </Card>
          </Col>
        </Row>

        <Card
          title={<span style={{ fontWeight: 600 }}>Texto enviado (vista previa)</span>}
          style={{ borderRadius: 12, marginBottom: 24 }}
          styles={{ body: { padding: "16px 20px" } }}
        >
          <div
            style={{
              maxHeight: 220,
              overflow: "auto",
              padding: "14px 16px",
              borderRadius: 8,
              background: "#fafafa",
              border: "1px solid #f0f0f0",
              fontSize: 14,
              lineHeight: 1.65,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {textoPreview || "—"}
          </div>
        </Card>

        <Card title={<span style={{ fontWeight: 600 }}>Resumen de respuestas</span>} style={{ borderRadius: 12, marginBottom: 24 }} styles={{ body: { padding: "18px 20px 22px" } }}>
          <Space wrap size={[12, 12]}>
            <Tag color="blue" style={{ fontSize: 13, padding: "6px 12px", margin: 0 }}>
              Enviados plantilla: {conPlantillaOk.length}
            </Tag>
            <Tag color="success" style={{ fontSize: 13, padding: "6px 12px", margin: 0 }}>
              Sí: {counts.si ?? 0}
            </Tag>
            <Tag color="orange" style={{ fontSize: 13, padding: "6px 12px", margin: 0 }}>
              No: {counts.no ?? 0}
            </Tag>
            <Tag style={{ fontSize: 13, padding: "6px 12px", margin: 0 }}>
              Sin respuesta: {counts.sin_respuesta ?? 0}
            </Tag>
            <Tag color="error" style={{ fontSize: 13, padding: "6px 12px", margin: 0 }}>
              Error envío: {counts.error_plantilla ?? 0}
            </Tag>
          </Space>
        </Card>

        <Collapse
          bordered={false}
          style={{ background: "transparent" }}
          defaultActiveKey={["enviados", "si", "no", "sin", "err"]}
          items={[
            {
              key: "enviados",
              label: (
                <Text strong>
                  A quién se envió la plantilla ({conPlantillaOk.length}) — excluye fallos de envío
                </Text>
              ),
              children: (
                <div style={{ borderRadius: 10, border: "1px solid #f0f0f0", overflow: "hidden" }}>
                  <Table
                    rowKey={(r) => `${r.numero}-${r.cuenta}`}
                    size="middle"
                    pagination={tablePagination}
                    scroll={{ x: 720 }}
                    columns={[...destCols, estadoCol]}
                    dataSource={conPlantillaOk}
                  />
                </div>
              ),
            },
            {
              key: "si",
              label: <Text strong>Quisieron recibir el contenido — SÍ ({si.length})</Text>,
              children: (
                <div style={{ borderRadius: 10, border: "1px solid #f0f0f0", overflow: "hidden" }}>
                  <Table rowKey={(r) => `${r.numero}-${r.cuenta}`} size="middle" columns={destCols} dataSource={si} pagination={tablePagination} scroll={{ x: 560 }} />
                </div>
              ),
            },
            {
              key: "no",
              label: <Text strong>No quisieron recibir — NO ({no.length})</Text>,
              children: (
                <div style={{ borderRadius: 10, border: "1px solid #f0f0f0", overflow: "hidden" }}>
                  <Table rowKey={(r) => `${r.numero}-${r.cuenta}`} size="middle" columns={destCols} dataSource={no} pagination={tablePagination} scroll={{ x: 560 }} />
                </div>
              ),
            },
            {
              key: "sin",
              label: <Text strong>Sin responder a la plantilla ({sinResp.length})</Text>,
              children: (
                <div style={{ borderRadius: 10, border: "1px solid #f0f0f0", overflow: "hidden" }}>
                  <Table rowKey={(r) => `${r.numero}-${r.cuenta}`} size="middle" columns={destCols} dataSource={sinResp} pagination={tablePagination} scroll={{ x: 560 }} />
                </div>
              ),
            },
            {
              key: "err",
              label: <Text strong>Error al enviar la plantilla ({errEnvio.length})</Text>,
              children: (
                <div style={{ borderRadius: 10, border: "1px solid #f0f0f0", overflow: "hidden" }}>
                  <Table rowKey={(r) => `${r.numero}-${r.cuenta}`} size="middle" columns={errCols} dataSource={errEnvio} pagination={tablePagination} scroll={{ x: 800 }} />
                </div>
              ),
            },
          ]}
        />
      </>
    );
  };

  if (!enabled) {
    return (
      <div className="p-4">
        <Alert
          type="info"
          showIcon
          message="La difusión por noticias no está habilitada para esta cooperativa. Pedile a un administrador que agregue su id en REACT_APP_NOTICIA_ENABLED_COOPS (frontend) y NOTICIA_ENABLED_COOPS (backend)."
        />
      </div>
    );
  }

  const tabLista = (
    <>
      <Alert
        type="info"
        showIcon
        className="mb-4"
        message="Elegí los socios que recibirán la difusión. Guardá la lista antes de cambiar de pestaña si querés conservarla en el servidor."
      />
      <Space wrap className="mb-3" style={{ width: "100%" }}>
        <Input
          placeholder="Buscar teléfono"
          prefix={<SearchOutlined />}
          value={searchNumero}
          onChange={(e) => setSearchNumero(e.target.value)}
          allowClear
        />
        <Input
          placeholder="Buscar cuenta"
          prefix={<SearchOutlined />}
          value={searchCuenta}
          onChange={(e) => setSearchCuenta(e.target.value)}
          allowClear
        />
        <Input
          placeholder="Buscar nombre"
          prefix={<SearchOutlined />}
          value={searchNombre}
          onChange={(e) => setSearchNombre(e.target.value)}
          allowClear
        />
      </Space>

      <Table
        rowKey="key"
        columns={columns}
        dataSource={filteredMensajes}
        pagination={{ pageSize: 8 }}
        title={() => (
          <Checkbox
            checked={areAllSelected}
            indeterminate={!areAllSelected && filteredMensajes.some((r) => isRecordSelected(r))}
            onChange={(e) => handleSelectAll(e.target.checked)}
          >
            Seleccionar visibles
          </Checkbox>
        )}
      />

      <Card title="Destinatarios seleccionados" className="mt-4 mb-0">
        <div
          className="mb-3"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Space wrap>
            <Input
              placeholder="Filtrar teléfono"
              value={selectedSearchNumero}
              onChange={(e) => setSelectedSearchNumero(e.target.value)}
              allowClear
            />
            <Input
              placeholder="Filtrar cuenta"
              value={selectedSearchCuenta}
              onChange={(e) => setSelectedSearchCuenta(e.target.value)}
              allowClear
            />
            <Input
              placeholder="Filtrar nombre"
              value={selectedSearchNombre}
              onChange={(e) => setSelectedSearchNombre(e.target.value)}
              allowClear
            />
          </Space>
          <Button type="default" icon={<PlusOutlined />} onClick={openModalManual}>
            Agregar manualmente
          </Button>
        </div>
        <Table
          rowKey="key"
          columns={selectedColumns}
          dataSource={filteredSelectedNumeros}
          pagination={{ pageSize: 8 }}
        />
        <Button type="default" onClick={handleGuardarLista} className="mt-2">
          Guardar lista
        </Button>
      </Card>

      <Modal
        title="Agregar destinatario manual"
        open={modalManualOpen}
        onCancel={handleModalManualCancel}
        destroyOnClose
        width={440}
        footer={null}
      >
        <Form form={formManual} layout="vertical" preserve={false} onFinish={submitManualRecipient}>
          <Form.Item
            name="nombre"
            label="Nombre"
            rules={
              manualCoincidencias.length > 0
                ? []
                : [{ required: true, whitespace: true, message: "Ingresá el nombre." }]
            }
          >
            <Input
              placeholder={
                manualCoincidencias.length > 0
                  ? "Se usa el nombre de la cuenta elegida abajo"
                  : "Nombre o razón social"
              }
              allowClear
              disabled={manualCoincidencias.length > 0}
            />
          </Form.Item>
          <Form.Item
            name="telefono"
            label="Teléfono"
            rules={[{ required: true, message: "Ingresá el teléfono." }]}
            extra="Se normaliza a formato móvil Argentina (549…). Podés escribir solo código de área y número, con o sin el 9 inicial."
          >
            <Input
              placeholder="Ej: 3534226477 o 93534226477"
              inputMode="tel"
              allowClear
              onChange={() => {
                if (manualCoincidencias.length > 0 || manualCuentaSeleccionada) {
                  setManualCoincidencias([]);
                  setManualCuentaSeleccionada(null);
                }
              }}
            />
          </Form.Item>
          {manualCoincidencias.length > 0 && (
            <>
              <Alert
                type="warning"
                showIcon
                className="mb-2"
                message="Ese teléfono ya existe en la lista superior."
                description="Seleccioná la cuenta que querés agregar sin salir del modal."
              />
              <div style={{ maxHeight: 220, overflow: "auto", border: "1px solid #f0f0f0", borderRadius: 6, padding: 8 }}>
                <Radio.Group
                  style={{ width: "100%" }}
                  value={manualCuentaSeleccionada}
                  onChange={(e) => setManualCuentaSeleccionada(e.target.value)}
                >
                  <Space direction="vertical" style={{ width: "100%" }}>
                    {manualCoincidencias.map((item) => (
                      <Radio key={item.key} value={item.key} style={{ width: "100%" }}>
                        <div>
                          <Text strong>Tel:</Text> {item.numero} | <Text strong>Cuenta:</Text> {item.cuenta}
                          <br />
                          <Text strong>Nombre:</Text> {item.nombre || "—"}
                        </div>
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
              </div>
            </>
          )}
          <div style={{ textAlign: "right", marginTop: 8 }}>
            <Space>
              <Button onClick={handleModalManualCancel}>Cancelar</Button>
              <Button type="primary" htmlType="submit">
                {manualCoincidencias.length > 0 ? "Seleccionar cuenta" : "Agregar"}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </>
  );

  const tabDifusion = (
    <div style={{ maxWidth: 880, margin: "0 auto" }}>
      <Alert
        type="info"
        showIcon
        message="Cómo funcionan las respuestas SÍ / NO"
        description="Si alguien todavía no respondió SÍ/NO, las nuevas difusiones solo se encolan: no se envía otra plantilla hasta que responda. Con un solo SÍ recibe todo lo pendiente en orden. Un NO aplica solo al mensaje al que respondió. El historial requiere el callback del bot al backend."
        style={{
          borderRadius: 10,
          marginBottom: 24,
          border: "1px solid #e6f4ff",
        }}
      />
      <Card
        bordered={false}
        style={{
          borderRadius: 12,
          background: "#fff",
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 32px rgba(15, 23, 42, 0.08)",
        }}
        styles={{ body: { padding: "28px 32px 32px" } }}
      >
        <Title level={5} style={{ marginTop: 0, marginBottom: 8, fontWeight: 600 }}>
          Texto de la noticia
        </Title>
        <Text type="secondary" style={{ display: "block", marginBottom: 16, fontSize: 14 }}>
          Podés usar solo texto, solo archivo (PDF o imagen) o ambos.
        </Text>
        <TextArea
          rows={8}
          value={cuerpoTexto}
          onChange={(e) => setCuerpoTexto(e.target.value)}
          placeholder="Escribí acá el contenido que verán quienes confirmen con SÍ…"
          style={{
            minHeight: 176,
            fontSize: 15,
            lineHeight: 1.65,
            borderRadius: 8,
            resize: "vertical",
            padding: "12px 14px",
          }}
        />

        <Divider style={{ margin: "28px 0 20px" }} />

        <Title level={5} style={{ marginBottom: 12, fontWeight: 600 }}>
          Archivo adjunto (opcional)
        </Title>
        <div
          style={{
            border: "1px dashed #d9d9d9",
            borderRadius: 10,
            padding: "22px 24px",
            background: "linear-gradient(180deg, #fafafa 0%, #f5f5f5 100%)",
            textAlign: mediaKey ? "left" : "center",
          }}
        >
          <Upload
            maxCount={1}
            customRequest={customUpload}
            accept=".pdf,image/jpeg,image/png,image/webp"
            onRemove={() => {
              setMediaKey(null);
              setMediaType(null);
              setMediaFileName(null);
            }}
            fileList={
              mediaKey
                ? [{ uid: "-1", name: mediaFileName || mediaKey, status: "done" }]
                : []
            }
          >
            <Button icon={<UploadOutlined />} size="large">
              Adjuntar PDF o imagen
            </Button>
          </Upload>
          {mediaKey && (
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #e8e8e8" }}>
              <Text type="secondary" style={{ fontSize: 13, wordBreak: "break-word" }}>
                <Text strong>Archivo listo:</Text> {mediaFileName || mediaKey}
              </Text>
            </div>
          )}
        </div>

        <Divider style={{ margin: "28px 0 0" }} />

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
            marginTop: 20,
          }}
        >
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={enviando}
            onClick={handleEnviar}
            size="large"
            style={{ minWidth: 200, height: 44, fontWeight: 500 }}
          >
            Enviar difusión
          </Button>
        </div>
      </Card>
    </div>
  );

  const tabHistorial = (
    <>
      <Button className="mb-3" onClick={loadHistorial} loading={historialLoading}>
        Actualizar historial
      </Button>
      <Table
        rowKey="_id"
        loading={historialLoading}
        columns={historialColumns}
        dataSource={historial}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 960 }}
      />
    </>
  );

  if (vistaDetalleActiva) {
    return (
      <div className="p-4" style={{ background: "#f5f6f8", minHeight: "calc(100vh - 48px)" }}>
        <Card
          bordered={false}
          style={{
            maxWidth: 1320,
            margin: "0 auto",
            borderRadius: 12,
            boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 32px rgba(15, 23, 42, 0.08)",
          }}
          styles={{ body: { padding: "20px 28px 36px" } }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
              marginBottom: 28,
              paddingBottom: 20,
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={closeDetalleVista} style={{ paddingLeft: 0 }}>
              Volver al historial
            </Button>
            <Divider type="vertical" style={{ height: 28, margin: "0 4px" }} />
            <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
              Detalle de difusión
            </Title>
          </div>
          {renderVistaDetalleCompleta()}
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4" style={{ background: "#f5f6f8", minHeight: "calc(100vh - 48px)" }}>
      <Card
        title={
          <span style={{ fontSize: 18, fontWeight: 600, letterSpacing: "-0.02em" }}>
            Difundir — Noticia
          </span>
        }
        bordered={false}
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          borderRadius: 12,
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 24px rgba(15, 23, 42, 0.06)",
        }}
        styles={{ body: { padding: "20px 24px 28px" } }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="large"
          tabBarStyle={{ marginBottom: 8 }}
          items={[
            {
              key: "lista",
              label: (
                <span>
                  <UnorderedListOutlined /> Lista de difusión
                </span>
              ),
              children: tabLista,
            },
            {
              key: "difusion",
              label: (
                <span>
                  <SendOutlined /> Contenido y envío
                </span>
              ),
              children: tabDifusion,
            },
            {
              key: "historial",
              label: (
                <span>
                  <HistoryOutlined /> Historial
                </span>
              ),
              children: tabHistorial,
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default CDifundirNoticia;
